import { Button } from '@connected/ui/components/button';
import { Skeleton } from '@connected/ui/components/skeleton';
import { Table } from '@connected/ui/components/table';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2, X } from 'lucide-react';
import { useRef, useState } from 'react';

import type { ElectricalProjectFile } from '@/hooks/use-electrical-project-files';

import { useElectricalProjectFileDelete } from '@/hooks/use-electrical-project-file-delete';
import { useElectricalProjectFilePreview } from '@/hooks/use-electrical-project-file-preview';
import { useElectricalProjectFileUpload } from '@/hooks/use-electrical-project-file-upload';
import { useElectricalProjectFiles } from '@/hooks/use-electrical-project-files';
import { humanFileSize } from '@/lib/utils';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/test/project-files/',
)({
    component: RouteComponent,
});

export function RouteComponent() {
    return (
        <div>
            <h1>Project Files</h1>
            <FileUpload />
            <FileTable />
        </div>
    );
}

export function FileUpload() {
    const inputRef = useRef<HTMLInputElement>(null);

    const [file, setFile] = useState<File | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [recordId, setRecordId] = useState<string | null>(null);

    const { upload, accept } = useElectricalProjectFileUpload({
        endpoint: '/api/v2/electrical/projects/files/upload/distributionboard',
    });

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (!file) {
            setErrors(['File is required']);
            return;
        }

        const result = await upload({
            file,
            query: {},
        });

        setErrors([]);
        setRecordId(result.RecordId);
        setFile(null);

        if (inputRef.current) {
            inputRef.current.value = '';
        }
    }

    return (
        <header className="flex items-center justify-between">
            <h2>Upload</h2>
            <form onSubmit={handleSubmit}>
                <input type="file" ref={inputRef} accept={accept ?? undefined} onChange={e => setFile(e.target.files?.[0] ?? null)} />
                <Button type="submit">Upload</Button>
            </form>
            {recordId && (
                <p>
                    RecordId:
                    {recordId}
                </p>
            )}
            {errors.length > 0 && <p className="text-red-500">{errors.join(', ')}</p>}
        </header>
    );
}

export function FileTable() {
    const { files } = useElectricalProjectFiles();

    return (
        <Table>
            <Table.Header>
                <Table.Row>
                    <Table.Cell>Order</Table.Cell>
                    <Table.Cell>Preview</Table.Cell>
                    <Table.Cell>Url</Table.Cell>
                    <Table.Cell>Is Local</Table.Cell>
                    <Table.Cell>File Name</Table.Cell>
                    <Table.Cell>File Type</Table.Cell>
                    <Table.Cell>File Size</Table.Cell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {files.map(file => (
                    <FileTableRow
                        key={file.ElectricalProjectFileId}
                        file={file}
                    />
                ))}
            </Table.Body>
        </Table>
    );
}

export function FileTableRow({ file }: { file: ElectricalProjectFile }) {
    const { isLoading, isError, url, metadata } = useElectricalProjectFilePreview({
        query: {
            ElectricalProjectFileId: file.ElectricalProjectFileId ?? '',
            FileId: file.File?.FileId ?? '',
        },
    });

    const { delete: deleteFile } = useElectricalProjectFileDelete({
        mutationOptions: {},
    });

    return (
        <Table.Row>
            <Table.Cell>{file.Order}</Table.Cell>
            <Table.Cell>
                {isLoading
                    ? (
                            <Skeleton className="w-16 h-16 flex items-center justify-center">
                                <Loader2 className="w-4 h-4 animate-spin" />
                            </Skeleton>
                        )
                    : isError
                        ? (
                                <Skeleton className="w-16 h-16 flex items-center justify-center">
                                    <X className="w-4 h-4" />
                                </Skeleton>
                            )
                        : metadata
                            ? (
                                    <div className="w-16 h-16 flex items-center justify-center">
                                        <img src={url} alt={metadata.name ?? ''} className="w-full h-full object-contain" />
                                    </div>
                                )
                            : null}
            </Table.Cell>
            <Table.Cell>{url}</Table.Cell>
            <Table.Cell>{file.File?.FileId ? 'No' : 'Yes'}</Table.Cell>
            <Table.Cell>{file.File?.Name}</Table.Cell>
            <Table.Cell>{file.File?.ContentType}</Table.Cell>
            <Table.Cell>{humanFileSize(file.File?.Size ?? 0, { base: 1000, binary: false, round: 2 })}</Table.Cell>
            <Table.Cell>
                <Button onClick={() => deleteFile(file.ElectricalProjectFileId!)}>
                    Delete
                </Button>
            </Table.Cell>
        </Table.Row>
    );
}
